import type { Ref } from "react";
import { Alert, Anchor, Box, Group, Paper, Stack, Text, ThemeIcon } from "@mantine/core";
import { BookOpen, Check, TriangleAlert } from "lucide-react";
import { Markdown } from "@nodus/ui";
import type { GuideStep } from "@/lib/api/guides";
import { ArticleAttachments } from "@/components/faq/article-attachments";
import { GuideBlock } from "./blocks/registry";
import { blockDoneMeta, DEFAULT_DONE_META, DEFAULT_DONE_SUMMARY, HIDDEN_TEXT_NOTE } from "./blocks/done";
import { StepIllustration } from "./illustrations";

/**
 * A step's content under its checklist row: its own Markdown, the FAQ article
 * it shows (rendered live, with attachments), or a live block. A built-in step
 * can add a picture above its text and an orange warning callout under it.
 */
export function GuideStepBody({
  step,
  onCompleted,
  guideKey,
  stepNumber,
}: {
  step: GuideStep;
  onCompleted?: () => void;
  /** The guide the step is in (for a block that saves its step itself). */
  guideKey?: string;
  /** "Step N" of another step in this guide, by key. */
  stepNumber?: (stepKey: string) => number | null;
}) {
  if (step.content_kind === "faq_ref" && step.ref) {
    return (
      <Paper withBorder radius="md" p="sm" bg="var(--mantine-color-default-hover)">
        <Group gap={4} mb={6} c="dimmed">
          <BookOpen size={12} />
          <Text fz="xs" c="dimmed">
            From the FAQ · {step.ref.category}
          </Text>
        </Group>
        <Markdown size="sm">{step.ref.content}</Markdown>
        <ArticleAttachments attachments={step.ref.attachments} />
      </Paper>
    );
  }
  const callout = step.callout?.trim();
  return (
    <Stack gap="xs">
      <StepIllustration name={step.illustration} />
      {step.body ? <Markdown size="sm">{step.body}</Markdown> : null}
      {callout ? (
        <Alert color="orange" variant="light" icon={<TriangleAlert size={18} />} role="alert">
          <Markdown size="sm">{callout}</Markdown>
        </Alert>
      ) : null}
      {step.content_kind === "block" && step.block ? (
        <Box>
          <GuideBlock
            blockKey={step.block_key}
            config={step.block}
            onCompleted={onCompleted}
            practice={step.practice === true}
            guideKey={guideKey}
            stepKey={step.key}
            done={step.done}
            stepNumber={stepNumber}
          />
        </Box>
      ) : null}
    </Stack>
  );
}

/**
 * A done step whose block is a form or an action (#436 done states): a short
 * ✓ summary instead of the form, and "Change this" to show it again. The
 * block's own `DoneView` replaces the summary when it has one. When the step
 * has its own text, one line says it is hidden too.
 */
export function GuideStepDoneSummary({
  step,
  onChange,
  controls,
  buttonRef,
}: {
  step: GuideStep;
  onChange: () => void;
  /** The id of the element Change this shows (aria-controls). */
  controls?: string;
  buttonRef?: Ref<HTMLButtonElement>;
}) {
  const meta = step.content_kind === "block" ? blockDoneMeta(step.block_key) : DEFAULT_DONE_META;
  const DoneView = meta.DoneView;
  return (
    <Paper withBorder radius="md" p="sm" bg="var(--mantine-color-teal-light)">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon color="teal" radius="xl" size={22} style={{ flexShrink: 0 }} aria-hidden>
          <Check size={14} />
        </ThemeIcon>
        <Stack gap={4} miw={0} flex={1} align="flex-start">
          {DoneView ? (
            <DoneView config={step.block ?? {}} />
          ) : (
            <Text fz="sm" fw={500}>
              {meta.summary ?? DEFAULT_DONE_SUMMARY}
            </Text>
          )}
          {step.body.trim() && (
            <Text fz="sm" c="dimmed">
              {HIDDEN_TEXT_NOTE}
            </Text>
          )}
          <Anchor
            component="button"
            type="button"
            fz="sm"
            ref={buttonRef}
            aria-expanded={false}
            aria-controls={controls}
            onClick={onChange}
          >
            Change this
          </Anchor>
        </Stack>
      </Group>
    </Paper>
  );
}
